import React, {useEffect, useRef, useState} from 'react';
import {Box, Grid} from '@mui/material';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../shared/types';
import '../ViewCategorization.scss';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {useCategorization} from '../../../hooks/categorization/useCategorization';
import {TrainingSet} from '../ViewCategorization';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';

type Props = {
  selectedTrainingSet: TrainingSet | null;
  setSelectedTrainingSet: React.Dispatch<
    React.SetStateAction<TrainingSet | null>
  >;
};
export interface ListItem extends TreeViewBaseItem<ExtendedTreeItemProps> {
  selected?: boolean;
  children?: ListItem[];
  parent?: string | null;
  type?: string;
  isGroup?: string;
  isLocked?: boolean;
  text?: string;
  securityGroupId?: string | null;
}

// Define common item shapes from API
export interface ApiItem {
  id: string;
  text: string;
  parent?: string;
  type?: string;
  isGroup?: string;
  isLocked?: boolean;
  securityGroupId?: string;
}

export interface SelectedItem {
  id: string;
  text: string;
  securityGroupId?: string;
}

// Helper: Build hierarchical (tree-style) structure
//
export const buildTree = (
  items: ApiItem[] = [],
  selectedItems: string[] = []
): {roots: ListItem[]; selectedSet: Set<string>} => {
  const map = new Map<string, ListItem>();
  const roots: ListItem[] = [];

  // Create node map
  for (const item of items) {
    map.set(item.id, {
      id: item.id,
      label: item.text,
      //selected: selectedItems.some((sel: SelectedItem) => sel.id === item.id),
      parent: item.parent ?? null,
      type: item.type ?? '',
      isGroup: item.isGroup ?? 'false',
      isLocked: item.isLocked ?? false,
      text: item.text ?? item.id,
      children: [],
      securityGroupId: item.securityGroupId ?? null,
    });
  }
  const selectedSet = new Set(selectedItems);

  // Establish parent-child relationships
  for (const item of items) {
    const node = map.get(item.id);
    if (!node) continue;

    if (item.parent && item.parent !== '#' && map.has(item.parent)) {
      if (selectedSet.has(item.id)) {
        selectedSet.add(item.parent);
      }
      map.get(item.parent)!.children!.push(node);
    } else {
      if (selectedSet.has(item.id)) {
        selectedSet.add(node.id);
      }
      roots.push(node);
    }
  }

  return {roots, selectedSet};
};

export const options: ISelectItem[] = [
  {id: '1', name: 'Tag', trainingSetType: 'Tag'},
  {id: '2', name: 'Folder', trainingSetType: 'Folder'},
  {id: '3', name: 'Assignment', trainingSetType: 'Assignment'},
  {id: '4', name: 'Saved Search', trainingSetType: 'SavedSearch'},
  {id: '5', name: 'Security Group', trainingSetType: 'SecurityGroup'},
];

export default React.memo(function CategorizationStep1({
  selectedTrainingSet,
  setSelectedTrainingSet,
}: Props) {
  const handleSelected = (values: string[] | null) => {
    setSelectedTrainingSet(
      (prev) =>
        ({
          ...prev,
          ids: values || [],
        }) as TrainingSet
    );
  };

  const {trainingSetType, trainingSetName} = useSelector(
    (state: any) => state.categorization
  );

  const optionsRef = useRef(
    !trainingSetType
      ? [...options]
      : [
          ...options,
          {
            id: 6,
            name: trainingSetName,
            trainingSetType: trainingSetType,
          },
        ]
  );

  const [treeData, setTreeData] = useState<ListItem[]>([]);
  const [, setLoading] = useState(false);
  const [defaultIdsWithTreeId, setDefaultIdsWithTreeId] = useState<
    string[] | null
  >(null);
  const [securityGroupId, setSecurityGroupId] = useState<number>(0);

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  useEffect(() => {
    if (loginUserData) {
      setSecurityGroupId(loginUserData?.securityGroupId);
    }
  }, [loginUserData]);
  // Numeric defaults
  const selectedProviewId: number = 0;
  const selectedProductionId: number = 0;
  const selectedSecurityGroupId: number = 0;

  const {
    securityGroupView,
    tagTreeView,
    folderTreeView,
    searchTreeView,
    assignmentTreeView,
  } = useCategorization();

  useEffect(() => {
    if (
      selectedTrainingSet?.trainingSet &&
      selectedTrainingSet.trainingSet.trainingSetType !== 'ExternalSource'
    ) {
      fetchTreeData(selectedTrainingSet.trainingSet);
    }
  }, [selectedTrainingSet?.trainingSet]);

  const handleDropdownChange = (key: ISelectItem) => {
    setSelectedTrainingSet({
      ids: selectedTrainingSet?.ids || [],
      trainingSet: key,
    });
    setLoading(true);
  };

  const fetchTreeData = (key: ISelectItem) => {
    // Form payload based on key
    let payload;

    switch (key.id) {
      case '1': // tag tree
        payload = {
          securityGroupId: securityGroupId,
          toggelValue: selectedProviewId,
        };
        break;
      case '2': // Folder Tree
        payload = {
          securityGroupId: securityGroupId,
          toggelValue: selectedProviewId,
        };
        break;
      case '3': // Assigmentment Tree
        payload = {
          securityGropuId: securityGroupId,
        };
        break;
      case '4': // Search Tree (no payload needed)
        payload = undefined;
        break;
      case '5': // Assignment Tree
        payload = {
          proviewId: selectedProviewId,
          productionId: selectedProductionId,
          securityGroupId: selectedSecurityGroupId,
        };
        break;
      default:
        console.error('Invalid key');
        setLoading(false);
        return;
    }

    let mutation;
    switch (key.id) {
      case '1':
        mutation = tagTreeView;
        break;
      case '2':
        mutation = folderTreeView;
        break;
      case '3':
        mutation = assignmentTreeView;
        break;
      case '4':
        mutation = searchTreeView;
        break;
      case '5':
        mutation = securityGroupView;

        break;
    }

    // Call the mutation
    mutation.mutate(payload, {
      onSuccess: (data) => {
        if (key.id === '5') {
          const securityGroupList =
            data?.securityGroupList.map((item: {id: number}) => ({
              ...item,
              id: item.id.toString(),
            })) || [];
          setTreeData(securityGroupList);
        } else {
          const {roots, selectedSet} = buildTree(
            data,
            selectedTrainingSet?.ids || []
          );
          setTreeData(roots);
          setDefaultIdsWithTreeId(Array.from(selectedSet));
        }

        setLoading(false);
      },
      onError: () => {
        console.log('Failure');
        setLoading(false);
      },
    });
  };

  return (
    <Grid
      container
      direction="column"
      alignItems="flex-start"
      rowGap="2px"
      height="100%"
      data-testid="arr-accordion-title"
      onClick={(e) => e.stopPropagation()}
    >
      <Box
        className="typo-text subhead4_size Step1-title"
        aria-label={t('categorization.title')}
        data-testid="vc-view-categorization-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 26,
          height: 'auto',
          px: '24px',
          py: (theme) => theme.spacing(1.5),
        }}
      >
        <span tabIndex={0} data-testid={`categorization-step1-step1title`}>
          {t('categorization.step1title')}
        </span>
        <Box paddingLeft={1} display="flex" alignItems="center">
          <ArrTooltipWrapper
            title={t('categorization.step1Tooltip.title')}
            trigger={
              <InfoIcon
                fontSize="medium"
                aria-label="information"
                className="info-icon-small"
                data-testid="step1-tooltip-info"
                tabIndex={0}
              />
            }
          >
            <div data-testid="step1Tooltip-content">
              <p
                dangerouslySetInnerHTML={{
                  __html: t('categorization.step1Tooltip.content'),
                }}
                data-testid={`categorization-step1-p`}
              />
            </div>
          </ArrTooltipWrapper>
        </Box>
      </Box>
      <Box
        className="typo-text subhead4_size step1-dropdownBox"
        aria-label={t('categorization.title')}
        data-testid="vc-view-categorization-title"
      >
        <ArrSingleSelectDropdown
          dropdownLabel={t('categorization.trainingSetLabel')}
          dropdownItems={optionsRef.current} // the items array
          selectedValues={selectedTrainingSet?.trainingSet} // pass the object directly
          onSelectionChange={handleDropdownChange}
          sizeVariant="medium"
          dataTestId="asd-categorization-arr-source-type-dropdown"
          tabIndex={0}
        />
      </Box>
      <Box
        className="arr-tree-container"
        aria-label={t('categorization.title')}
        data-testid="vc-view-categorization-title"
      >
        <ArrTreeView
          treeSelectedItems={selectedTrainingSet?.ids || []}
          setSelectedItem={handleSelected}
          itemsList={treeData}
          isShowCheckbox={true}
          multipleSelection={true}
          isShowFileType={false}
          defaultExpandedIds={defaultIdsWithTreeId || []}
        />
      </Box>
    </Grid>
  );
});
